import type { MouseEvent } from "react";
import { useState } from "react";
import { Box, Popover, Stack, Typography, useTheme, Fade } from "@mui/material";
import { REACTION_TYPES, ReactionType, getReactionConfig } from "../../types/reaction.types";
import { useTranslation } from "react-i18next";
import { FoodsIconButton, FoodsTooltip } from "@foodpilot/foods";
import { ReactionIcon } from "../ReactionIcon";

interface PostActionsProps {
    post: {
        userReaction?: string;
        commentCount: number;
    };
    onReact: (type: ReactionType) => void;
    onComment: () => void;
    onShare: () => void;
}

export const PostActions = ({ post, onReact, onComment, onShare }: PostActionsProps) => {
    const theme = useTheme();
    const [reactionAnchorEl, setReactionAnchorEl] = useState<null | HTMLElement>(null);
    const showReactionPicker = Boolean(reactionAnchorEl);
    const { t } = useTranslation();

    const handleReactionHover = (event: MouseEvent<HTMLElement>) => {
        setReactionAnchorEl(event.currentTarget);
    };

    const handleReactionClose = () => {
        setReactionAnchorEl(null);
    };

    const handleReactionSelect = (type: ReactionType) => {
        onReact(type);
        handleReactionClose();
    };

    const userReaction = post.userReaction;
    const hasReaction = !!userReaction;
    const activeReaction = getReactionConfig(userReaction ?? "");
    const likeReaction = getReactionConfig("like");

    const pillSx = {
        px: 2,
        py: 0.75,
        borderRadius: 999,
        cursor: "pointer",
        transition: "background-color 0.15s ease, color 0.15s ease",
        "&:hover": {
            backgroundColor: theme.custom.grey[100],
        },
    };

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-around" gap={1} sx={{ pt: 1, mt: 1 }}>
            <Box sx={{ position: "relative" }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={0.75}
                    onClick={() => onReact("like")}
                    onMouseEnter={handleReactionHover}
                    sx={{
                        ...pillSx,
                        color: hasReaction ? activeReaction?.color || likeReaction?.color : theme.custom.grey[1400],
                        backgroundColor:
                            hasReaction ? `${activeReaction?.color ?? likeReaction?.color}14` : "transparent",
                    }}
                >
                    {hasReaction && activeReaction && "muiIcon" in activeReaction && activeReaction.muiIcon ?
                        <Box component="span" sx={{ display: "inline-flex", color: activeReaction.color }}>
                            <ReactionIcon config={activeReaction} size={1} />
                        </Box>
                    :   <FoodsIconButton
                            size="xs"
                            icon={hasReaction ? (activeReaction?.icon ?? "starFilled") : "starEmpty"}
                            buttonProps={{ component: "span" }}
                        />
                    }
                    <Typography variant="body2" sx={{ fontWeight: hasReaction ? 600 : 400 }}>
                        {t("social.actions.like", "Like")}
                    </Typography>
                </Stack>

                <Popover
                    open={showReactionPicker}
                    anchorEl={reactionAnchorEl}
                    onClose={handleReactionClose}
                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                    transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                    TransitionComponent={Fade}
                    TransitionProps={{ timeout: 200 }}
                    sx={{
                        "& .MuiPopover-paper": {
                            borderRadius: "25px",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                            border: `1px solid ${theme.custom.grey[300]}`,
                            overflow: "visible",
                            mt: -1,
                        },
                    }}
                    onMouseLeave={handleReactionClose}
                >
                    <Box display="flex" alignItems="center" sx={{ p: 1, gap: 0.5 }} onMouseLeave={handleReactionClose}>
                        {REACTION_TYPES.map((reaction) => {
                            const isSelected = post.userReaction === reaction.type;

                            return (
                                <FoodsTooltip
                                    width="unset"
                                    key={reaction.type}
                                    title={t(reaction.labelKey, reaction.defaultLabel)}
                                    placement="top"
                                >
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => handleReactionSelect(reaction.type as ReactionType)}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "50%",
                                            width: 48,
                                            height: 48,
                                            border: "none",
                                            cursor: "pointer",
                                            backgroundColor: isSelected ? `${reaction.color}20` : "transparent",
                                            outline:
                                                isSelected ? `2px solid ${reaction.color}` : "2px solid transparent",
                                            "&:hover": {
                                                backgroundColor: `${reaction.color}20`,
                                                transform: "scale(1.15)",
                                                outline: `2px solid ${reaction.color}`,
                                            },
                                            transition: "all 0.2s ease",
                                        }}
                                    >
                                        <ReactionIcon config={reaction} size={2.5} />
                                    </Box>
                                </FoodsTooltip>
                            );
                        })}
                    </Box>
                </Popover>
            </Box>

            <Stack
                direction="row"
                alignItems="center"
                gap={0.75}
                onClick={onComment}
                sx={{ ...pillSx, color: theme.custom.grey[1400] }}
            >
                <FoodsIconButton size="xs" icon="tagComment" buttonProps={{ component: "span" }} />
                <Typography variant="body2">
                    {t("social.actions.comment", "Comment")}
                    {post.commentCount > 0 ? ` · ${post.commentCount}` : ""}
                </Typography>
            </Stack>

            <Stack
                direction="row"
                alignItems="center"
                gap={0.75}
                onClick={onShare}
                sx={{ ...pillSx, color: theme.custom.grey[1400] }}
            >
                <FoodsIconButton size="xs" icon="exportDownload" buttonProps={{ component: "span" }} />
                <Typography variant="body2">{t("social.actions.share", "Share")}</Typography>
            </Stack>
        </Stack>
    );
};
